import { useCallback, useEffect, useState } from 'react';
import { Grid, Paper, Typography, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Button from '@mui/material/Button';
import b64toBlob from 'b64-to-blob';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchIndividualProviderVisitPerformance, MenuProps, exportIndividualProviderVisitPerformance, fetchProvidersByRoles } from '../WidgetService';
import EngagementSummary from '../../../Models/Widget/EngagementSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { parentUrl } from '../../../utils/constants';
import { getDateFilter } from '../../HedisMetrics/HedisMetricService';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { dateFormatter } from '../../../utils/timeFormat';
import { generateRandomId } from '../../../utils/common';
import '../Widgets.css';

const IndividualProviderVisitPerformanceDetail = () => {
    const history = useNavigate();
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const {
        individualEngagementDateFilter,
        setIndividualEngagementDateFilter,
        individualEngagementFromDate,
        setIndividualEngagementFromDate,
        individualEngagementToDate,
        setIndividualEngagementToDate,
        individualEngagementSelectedProvider,
        setIndividualEngagementSelectedProvider,
        individualEngagementShowInActiveProviders,
        setIndividualEngagementShowInActiveProviders,
    } = useWidgetContext();
    const [members, setMembers] = useState<EngagementSummary>();
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [loadedPage, setLoadedPage] = useState(false);
    const [roleBasedProviders, setRoleBasedProviders] = useState<any[]>([]);
    const [filteredProviders, setFilteredProviders] = useState<any[]>([]);
    const dateFilterList = getDateFilter();

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
        const data = await fetchProvidersByRoles();
        setRoleBasedProviders(data);
        var filteredProvider = individualEngagementShowInActiveProviders ? data : data.filter((pro: any) => pro.Role.RoleName !== 'Inactive');
        setFilteredProviders(filteredProvider);
    }, [isFetched]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (roleBasedProviders?.length) {
            setLoadedPage(true);
        }
    }, [roleBasedProviders]);

    useEffect(() => {
        const shouldFetch = loadedPage && (individualEngagementDateFilter !== TimePeriodFilterEnum.Custom || (individualEngagementFromDate && individualEngagementToDate));

        if (!shouldFetch) {
            return;
        }

        const changeFilter = async () => {
            setIsMembersLoading(true);
            const data = await fetchIndividualProviderVisitPerformance(
                individualEngagementSelectedProvider,
                individualEngagementDateFilter,
                individualEngagementFromDate && individualEngagementDateFilter === TimePeriodFilterEnum.Custom
                    ? new Date(individualEngagementFromDate).toISOString()
                    : '',
                individualEngagementToDate && individualEngagementDateFilter === TimePeriodFilterEnum.Custom
                    ? new Date(individualEngagementToDate).toISOString()
                    : '',
                individualEngagementShowInActiveProviders,
                true
            );
            setMembers(data);
            setIsMembersLoading(false);
            setIsFetched(true);
        };

        changeFilter();
    }, [
        individualEngagementSelectedProvider,
        individualEngagementDateFilter,
        individualEngagementFromDate,
        individualEngagementToDate,
        individualEngagementShowInActiveProviders,
        loadedPage,
    ]);

    const backToWidget = useCallback(() => {
        history('/provider/metricsummary/smart-dashboard');
    }, [history]);

    const changeFromDate = useCallback((date: any) => {
        setIndividualEngagementFromDate(date);
    }, [setIndividualEngagementFromDate]);

    const changeToDate = useCallback((date: any) => {
        setIndividualEngagementToDate(date);
    }, [setIndividualEngagementToDate]);

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportIndividualProviderVisitPerformance(
            individualEngagementSelectedProvider,
            individualEngagementDateFilter,
            individualEngagementFromDate && individualEngagementDateFilter === TimePeriodFilterEnum.Custom
                ? new Date(individualEngagementFromDate).toISOString()
                : '',
            individualEngagementToDate && individualEngagementDateFilter === TimePeriodFilterEnum.Custom
                ? new Date(individualEngagementToDate).toISOString()
                : '',
            individualEngagementShowInActiveProviders
        );

        const blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const today = new Date();
        const month = today.getMonth() + 1; // getMonth() is zero-based
        const day = today.getDate();
        const dateInfo = [(month > 9 ? '' : '0') + month, (day > 9 ? '' : '0') + day, today.getFullYear()].join('');
        link.setAttribute('download', `IndividualProviderVisitPerformance - ${dateInfo}.xlsx`);
        link.click();
        link.remove();

        setDownLoading(false);
    };

    const toggleProviderHandler = useCallback((isChecked: boolean) => {
        setIndividualEngagementShowInActiveProviders(isChecked);
        setIndividualEngagementSelectedProvider('All');
        if (isChecked) {
            var filteredProvider = roleBasedProviders;
            setFilteredProviders(filteredProvider);
        }
        else {
            var filteredProvider = roleBasedProviders.filter((pro: any) => pro.Role.RoleName !== 'Inactive');
            setFilteredProviders(filteredProvider);
        }
    }, [setIndividualEngagementShowInActiveProviders, setIndividualEngagementSelectedProvider, roleBasedProviders]);

    const columns: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            sortable: false,
            renderCell: (params) => {
                return <Typography>{params.api.getSortedRowIds().indexOf(params.id) + 1}</Typography>;
            },
        },
        {
            field: 'Name',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'DateInfo',
            headerName: 'Visited Date',
            width: 150,
            valueGetter: (value, row) => row.DateInfo ? dateFormatter(new Date(row.DateInfo)) : '-',
        },
        {
            field: 'ReasonForVisit',
            headerName: 'Reason For Visit',
            flex: 1,
        },
        {
            field: 'MeansOfEngagement',
            headerName: 'Means Of Engagement',
            flex: 1,
        },
        {
            field: 'Provider',
            headerName: 'Engaged Provider',
            flex: 1,
        },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display="inline-block" variant="h6" color="primary">
                            Individual Provider Visit Performance
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container pl={2} mt={0} spacing={2}>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Providers</InputLabel>
                            <Select
                                value={individualEngagementSelectedProvider}
                                label="Providers"
                                name="Providers"
                                onChange={(e) => setIndividualEngagementSelectedProvider(e.target.value)}
                                MenuProps={MenuProps}
                                renderValue={(selected) => {
                                    if (selected === 'All') {
                                        return 'All';
                                    }

                                    const provider = filteredProviders.find(
                                        (pro) => pro.Id === selected
                                    );

                                    return provider
                                        ? `${provider.LastName}, ${provider.FirstName}`
                                        : 'Provider';
                                }}
                            >
                                <MenuItem key="All" value="All">
                                    All
                                </MenuItem>
                                <FormControlLabel
                                    sx={{ pl: '12px', mb: '0px' }}
                                    control={
                                        <Checkbox
                                            checked={individualEngagementShowInActiveProviders}
                                            onChange={(e) => toggleProviderHandler(e.target.checked)}
                                        />
                                    }
                                    label="Show Inactive"
                                />
                                {filteredProviders.map((pro) => (
                                    <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                        {pro.LastName}, {pro.FirstName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={1.5}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Date Filter</InputLabel>
                            <Select
                                value={individualEngagementDateFilter}
                                label="Date Filter"
                                name="Date"
                                onChange={(e) => setIndividualEngagementDateFilter(parseInt(e.target.value))}
                            >
                                {dateFilterList.map((date) => (
                                    <MenuItem key={date.Id} value={date.Id}>
                                        {date.Value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {individualEngagementDateFilter === TimePeriodFilterEnum.Custom && (
                        <>
                            <Grid size={1.5}>
                                <DesktopDatePicker
                                    label="From Date"
                                    value={individualEngagementFromDate ?? null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={changeFromDate}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                            <Grid size={1.5}>
                                <DesktopDatePicker
                                    label="To Date"
                                    value={individualEngagementToDate ?? null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={changeToDate}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid size={individualEngagementDateFilter === TimePeriodFilterEnum.Custom ? 4.7 : 7.7} />
                    <Grid size={1}>
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => exportDetail()}
                            variant="contained"
                            color="success"
                            disabled={downLoading || members?.TotalVisitMembers?.length === 0}
                        >
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} minHeight="60vh" sx={{ position: 'relative' }}>
                        {!isMembersLoading && members ? (
                            <Grid container columnSpacing={3}>
                                <Grid size={6} textAlign="center">
                                    <Typography display="inline-block" variant="h6" color="primary">
                                        In-Person Visits ({members.InPersonVisitMembers?.length ?? 0})
                                    </Typography>
                                    <Grid size={12} sx={{ height: '67vh', width: '100%' }}>
                                        <DataGrid
                                            columns={columns}
                                            rows={members.InPersonVisitMembers ?? []}
                                            getRowId={(row) => `${row.ItemId}-${row.DateInfo}-${generateRandomId()}`}
                                            paginationMode="server"
                                            hideFooterPagination
                                            hideFooter
                                            onCellClick={(params: any) => {
                                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid size={6} textAlign="center">
                                    <Typography display="inline-block" variant="h6" color="primary">
                                        All Visits ({members.TotalVisitMembers?.length ?? 0})
                                    </Typography>
                                    <Grid size={12} sx={{ height: '67vh', width: '100%' }}>
                                        <DataGrid
                                            columns={columns}
                                            rows={members.TotalVisitMembers ?? []}
                                            getRowId={(row) => `${row.ItemId}-${generateRandomId()}`}
                                            paginationMode="server"
                                            hideFooterPagination
                                            hideFooter
                                            onCellClick={(params: any) => {
                                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        ) : (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default IndividualProviderVisitPerformanceDetail;
